import React, {
  useCallback, useContext, useEffect, useRef, useState,
} from 'react';
import {
  Box, Chip, Popover, Stack, Tooltip, Typography,
} from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { areaElementClasses } from '@mui/x-charts/LineChart';
import axios from 'axios';
import dayjs from 'dayjs';
import { AuthContext } from '../../components/signin/AuthContext';
import { useV4Resolve } from '../theme';

// ── Measure definitions ──────────────────────────────────────────────────────

const MEASURES = {
  Temperatur:   { apiKey: 'temperatur',   label: 'Temperatur',   unit: '°C',   axis: 'left',  area: false, defaultColor: '#ff6b6b' },
  Temperatur_2: { apiKey: 'temperatur_2', label: 'Temp. 2',      unit: '°C',   axis: 'left',  area: false, defaultColor: '#ff9e9e' },
  Humidity:     { apiKey: 'humidity',     label: 'Luftfeuchte',  unit: '%',    axis: 'left',  area: false, defaultColor: 'var(--v4-cyan)' },
  Humidity_2:   { apiKey: 'humidity_2',   label: 'Luftf. 2',     unit: '%',    axis: 'left',  area: false, defaultColor: '#48e5ff' },
  Co2:          { apiKey: 'co2',          label: 'CO₂',          unit: 'ppm',  axis: 'right', area: false, defaultColor: 'var(--v4-purple)' },
  Luftdruck:    { apiKey: 'luftdruck',    label: 'Luftdruck',    unit: 'hPa',  axis: 'left',  area: false, defaultColor: '#ffa300' },
  VPD:          { apiKey: 'vpd',          label: 'VPD',          unit: 'kPa',  axis: 'left',  area: false, defaultColor: '#dc0ab4' },
  Licht:        { apiKey: 'licht',        label: 'Licht',        unit: '%',    axis: 'left',  area: true,  defaultColor: 'var(--v4-gold)' },
  Abluft:       { apiKey: 'abluft',       label: 'Abluft',       unit: '%',    axis: 'left',  area: true,  defaultColor: 'var(--v4-blue)' },
  Umluft:       { apiKey: 'umluft',       label: 'Umluft',       unit: '%',    axis: 'left',  area: true,  defaultColor: '#d6924c' },
  Heizung:      { apiKey: 'heizung',      label: 'Heizung',      unit: '%',    axis: 'left',  area: true,  defaultColor: 'var(--v4-red)' },
};

const PRESET_COLORS = [
  '#ff6b6b','#ff9e3c','var(--v4-gold)','var(--v4-green)','var(--v4-cyan)',
  'var(--v4-blue)','var(--v4-purple)','#ff2d55','#dc0ab4','#00ff88',
  'var(--v4-teal)','#b3d4ff','#ffffff','#ff8c00','#c0c0c0',
];

const SENSOR_KEYS = ['Temperatur','Temperatur_2','Humidity','Humidity_2','Co2','Luftdruck','VPD'];
const ACTOR_KEYS  = ['Licht','Abluft','Umluft','Heizung'];

// ── Area gradient defs ───────────────────────────────────────────────────────

function AreaGradients({ entries }) {
  return (
    <defs>
      {entries.map(([key, color]) => (
        <linearGradient key={key} id={`ag-${key}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor={color} stopOpacity={0.35} />
          <stop offset="100%" stopColor={color} stopOpacity={0.02} />
        </linearGradient>
      ))}
    </defs>
  );
}

// ── Color picker popover ─────────────────────────────────────────────────────

function ColorSwatch({ color, onChange }) {
  const resolve = useV4Resolve();
  const [anchor, setAnchor] = useState(null);
  return (
    <>
      <Box
        onClick={e => { e.stopPropagation(); setAnchor(e.currentTarget); }}
        sx={{
          width: 14, height: 14, borderRadius: '50%',
          background: resolve(color),
          border: '2px solid rgba(var(--v4-fg-rgb),0.25)',
          cursor: 'pointer', flexShrink: 0,
          transition: 'transform 0.15s',
          '&:hover': { transform: 'scale(1.3)' },
        }}
      />
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        PaperProps={{
          sx: {
            background: 'rgba(var(--v4-surface2-rgb),0.97)',
            border: '1px solid rgba(var(--v4-cyan-rgb),0.2)',
            borderRadius: '12px',
            backdropFilter: 'blur(24px)',
            p: 1.5,
          },
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, width: 154 }}>
          {PRESET_COLORS.map(c => (
            <Box
              key={c}
              onClick={() => { onChange(c); setAnchor(null); }}
              sx={{
                width: 20, height: 20, borderRadius: '50%',
                background: resolve(c),
                border: c === color ? '2px solid var(--v4-text)' : '2px solid transparent',
                cursor: 'pointer',
                transition: 'transform 0.12s',
                '&:hover': { transform: 'scale(1.25)' },
              }}
            />
          ))}
        </Box>
      </Popover>
    </>
  );
}

// ── Toggle chip for one measure ──────────────────────────────────────────────

function MeasureChip({ mkey, def, active, color: rawColor, onToggle, onColorChange }) {
  const color = useV4Resolve()(rawColor);
  return (
    <Box
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.6,
        px: 1.4, py: 0.55,
        borderRadius: '20px',
        cursor: 'pointer',
        userSelect: 'none',
        background: active ? `color-mix(in srgb, ${color} 13%, transparent)` : 'rgba(var(--v4-fg-rgb),0.03)',
        border: `1px solid ${active ? `color-mix(in srgb, ${color} 40%, transparent)` : 'rgba(var(--v4-fg-rgb),0.08)'}`,
        transition: 'all 0.18s',
        boxShadow: active ? `0 0 12px color-mix(in srgb, ${color} 20%, transparent)` : 'none',
        '&:hover': {
          background: active ? `color-mix(in srgb, ${color} 20%, transparent)` : 'rgba(var(--v4-fg-rgb),0.06)',
          border: `1px solid ${active ? `color-mix(in srgb, ${color} 60%, transparent)` : 'rgba(var(--v4-fg-rgb),0.14)'}`,
        },
      }}
      onClick={onToggle}
    >
      <ColorSwatch color={rawColor} onChange={onColorChange} />
      <Typography sx={{
        fontSize: '0.72rem', fontWeight: 700,
        color: active ? color : 'rgba(var(--v4-muted-rgb),0.45)',
        lineHeight: 1,
        transition: 'color 0.18s',
      }}>
        {def.label}
      </Typography>
      {def.unit && (
        <Typography sx={{
          fontSize: '0.58rem', fontWeight: 500,
          color: active ? `color-mix(in srgb, ${color} 60%, transparent)` : 'rgba(var(--v4-muted-rgb),0.2)',
          lineHeight: 1,
        }}>
          {def.unit}
        </Typography>
      )}
    </Box>
  );
}

// ── Main chart component ─────────────────────────────────────────────────────

export default function AnalyticsChart({ grow_setup, time_from, time_to }) {
  const { token } = useContext(AuthContext);
  const resolve = useV4Resolve();

  // Which measures are toggled on
  const [active, setActive] = useState({});
  // Per-measure colors (user can customize)
  const [colors, setColors] = useState(
    Object.fromEntries(Object.entries(MEASURES).map(([k, v]) => [k, v.defaultColor]))
  );
  // Raw API data: { Temperatur: { xaxis: [...], data: [...] }, ... }
  const [rawData, setRawData] = useState({});
  const [loading, setLoading] = useState(false);

  // Responsive width
  const containerRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(900);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver(entries => {
      setChartWidth(Math.floor(entries[0].contentRect.width));
    });
    obs.observe(el);
    setChartWidth(el.getBoundingClientRect().width || 900);
    return () => obs.disconnect();
  }, []);

  const chartHeight = Math.max(340, Math.min(520, chartWidth * 0.42));

  // Fetch all data when time range or grow_setup changes
  useEffect(() => {
    if (!grow_setup) return;
    setLoading(true);
    const start = new Date(time_from);
    const end   = new Date(time_to);
    axios.post('/get_all_measures_by_timedelta/', {
      start_time: start,
      end_time: end,
      grow_setup_id: grow_setup,
      aggregation_level: '5m',
    }, {
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    }).then(res => {
      setRawData(res.data || {});
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, [grow_setup, time_from, time_to]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleMeasure = useCallback((key) => {
    setActive(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const setColor = useCallback((key, color) => {
    setColors(prev => ({ ...prev, [key]: color }));
  }, []);

  // Build xAxis from first available active series
  const activeKeys = Object.entries(active).filter(([, on]) => on).map(([k]) => k);

  let xAxisData = [];
  for (const key of activeKeys) {
    const apiKey = MEASURES[key]?.apiKey;
    if (rawData[apiKey]?.xaxis?.length) {
      xAxisData = rawData[apiKey].xaxis.map(v => new Date(v));
      break;
    }
    // fallback: capitalize first char
    const capKey = key.charAt(0).toUpperCase() + key.slice(1);
    if (rawData[capKey]?.xaxis?.length) {
      xAxisData = rawData[capKey].xaxis.map(v => new Date(v));
      break;
    }
  }

  const xAxis = xAxisData.length > 0 ? [{
    data: xAxisData,
    scaleType: 'time',
    valueFormatter: v => dayjs(v).format('HH:mm DD.MM'),
    tickMinStep: 5 * 60 * 1000,
  }] : [];

  // Build series for active measures
  const areaGradEntries = [];
  const series = activeKeys.map(key => {
    const def = MEASURES[key];
    const color = resolve(colors[key]);
    const apiKey = def.apiKey;
    // API returns keys as-is (e.g. "temperatur", "humidity", "temperatur_2")
    const row = rawData[apiKey] ?? rawData[key] ?? rawData[key.toLowerCase()] ?? null;
    const data = row?.data ?? [];

    if (def.area) {
      areaGradEntries.push([key, color]);
    }

    return {
      id: key,
      label: def.label,
      data,
      color,
      yAxisId: def.axis === 'right' ? 'right-axis' : 'left-axis',
      area: def.area,
      showMark: false,
      curve: def.area ? 'linear' : 'monotoneX',
      valueFormatter: v => v != null ? `${v.toFixed(1)} ${def.unit}` : '—',
    };
  });

  // Y-axis auto-range helper
  function axisRange(axisId) {
    const vals = activeKeys
      .filter(k => (axisId === 'right' ? MEASURES[k].axis === 'right' : MEASURES[k].axis === 'left'))
      .flatMap(k => {
        const apiKey = MEASURES[k].apiKey;
        return (rawData[apiKey]?.data || rawData[k]?.data || []).filter(v => v != null);
      });
    if (!vals.length) return {};
    const mn = Math.min(...vals);
    const mx = Math.max(...vals);
    const pad = (mx - mn) * 0.12 || 5;
    return { min: Math.floor(mn - pad), max: Math.ceil(mx + pad) };
  }

  const leftRange  = axisRange('left');
  const rightRange = axisRange('right');

  const yAxis = [
    {
      id: 'left-axis',
      scaleType: 'linear',
      ...leftRange,
      tickLabelStyle: { fill: resolve('rgba(var(--v4-muted-rgb),0.55)'), fontSize: 11 },
    },
    {
      id: 'right-axis',
      scaleType: 'linear',
      position: 'right',
      ...rightRange,
      tickLabelStyle: { fill: 'color-mix(in srgb, var(--v4-purple) 33%, transparent)', fontSize: 11 },
    },
  ];

  const hasData = series.length > 0 && xAxisData.length > 0;

  return (
    <Box sx={{
      background: 'rgba(var(--v4-surface-rgb),0.92)',
      border: '1px solid rgba(var(--v4-cyan-rgb),0.12)',
      borderRadius: '20px',
      backdropFilter: 'blur(24px)',
      overflow: 'hidden',
    }}>
      {/* ── Chart area ── */}
      <Box ref={containerRef} sx={{ px: 2, pt: 2, pb: 0 }}>
        {loading && (
          <Box sx={{ height: chartHeight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ color: 'rgba(var(--v4-cyan-rgb),0.4)', fontSize: '0.8rem', letterSpacing: '0.1em' }}>
              Lade Daten…
            </Typography>
          </Box>
        )}

        {!loading && !hasData && (
          <Box sx={{ height: chartHeight, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1 }}>
            <Typography sx={{ color: 'rgba(var(--v4-cyan-rgb),0.2)', fontSize: '0.85rem' }}>
              {activeKeys.length === 0 ? 'Wähle Messwerte unten aus' : 'Keine Daten für den gewählten Zeitraum'}
            </Typography>
          </Box>
        )}

        {!loading && hasData && chartWidth > 0 && (
          <LineChart
            width={chartWidth - 16}
            height={chartHeight}
            series={series}
            xAxis={xAxis}
            yAxis={yAxis}
            leftAxis="left-axis"
            rightAxis="right-axis"
            grid={{ horizontal: true, vertical: false }}
            sx={{
              // axis lines + ticks
              '& .MuiChartsAxis-line':      { stroke: 'rgba(var(--v4-cyan-rgb),0.1)' },
              '& .MuiChartsAxis-tick':      { stroke: 'rgba(var(--v4-cyan-rgb),0.1)' },
              '& .MuiChartsAxis-tickLabel': { fill: 'rgba(var(--v4-muted-rgb),0.5)', fontSize: '11px' },
              // grid lines
              '& .MuiChartsGrid-horizontalLine': { stroke: 'rgba(var(--v4-cyan-rgb),0.06)' },
              // legend (hidden — we use our own)
              '& .MuiChartsLegend-root': { display: 'none' },
              // area fills via gradient
              ...Object.fromEntries(
                areaGradEntries.map(([k]) => [
                  `& .${areaElementClasses.root}[id*="${k}"]`,
                  { fill: `url('#ag-${k}')` },
                ])
              ),
              // tooltip
              '& .MuiChartsTooltip-root': {
                background: 'rgba(var(--v4-surface2-rgb),0.95)',
                border: '1px solid rgba(var(--v4-cyan-rgb),0.2)',
                borderRadius: '12px',
                backdropFilter: 'blur(20px)',
              },
              '& .MuiChartsTooltip-cell': { color: 'rgba(var(--v4-muted-rgb),0.9)', fontSize: '0.78rem' },
              '& .MuiChartsTooltip-labelCell': { color: 'rgba(var(--v4-cyan-rgb),0.7)' },
            }}
            slotProps={{ legend: { hidden: true } }}
          >
            <AreaGradients entries={areaGradEntries} />
          </LineChart>
        )}
      </Box>

      {/* ── Divider ── */}
      <Box sx={{ mx: 2.5, height: '1px', background: 'rgba(var(--v4-cyan-rgb),0.07)', my: 0 }} />

      {/* ── Toggle panel ── */}
      <Box sx={{ px: 2.5, py: 2 }}>
        {/* Sensors row */}
        <Typography sx={{
          fontSize: '0.58rem', fontWeight: 800, textTransform: 'uppercase',
          letterSpacing: '0.14em', color: 'rgba(var(--v4-cyan-rgb),0.4)', mb: 1,
        }}>
          Sensoren
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {SENSOR_KEYS.map(key => (
            <MeasureChip
              key={key}
              mkey={key}
              def={MEASURES[key]}
              active={!!active[key]}
              color={colors[key]}
              onToggle={() => toggleMeasure(key)}
              onColorChange={c => setColor(key, c)}
            />
          ))}
        </Box>

        {/* Actors row */}
        <Typography sx={{
          fontSize: '0.58rem', fontWeight: 800, textTransform: 'uppercase',
          letterSpacing: '0.14em', color: 'rgba(var(--v4-cyan-rgb),0.4)', mb: 1,
        }}>
          Aktoren
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {ACTOR_KEYS.map(key => (
            <MeasureChip
              key={key}
              mkey={key}
              def={MEASURES[key]}
              active={!!active[key]}
              color={colors[key]}
              onToggle={() => toggleMeasure(key)}
              onColorChange={c => setColor(key, c)}
            />
          ))}
        </Box>

        {/* Hint */}
        <Typography sx={{ fontSize: '0.62rem', color: 'rgba(var(--v4-muted-rgb),0.2)', mt: 1.5 }}>
          Klicke auf den farbigen Kreis neben einem Messwert um die Farbe zu ändern
        </Typography>
      </Box>
    </Box>
  );
}
